import type { ClientError } from '@tribeplatform/gql-client/lib'
import type {
  MutationUpdateNetworkNotificationSettingsArgs,
  Network,
  NotificationNetworkSettings,
} from '@tribeplatform/gql-client/types'

import {
  useMutation,
  UseMutationOptions,
} from '../../lib/react-query/useMutation.js'
import { useQueryClient } from '../../lib/react-query/useQueryClient.js'
import { useTribeClient } from '../../useTribeClient.js'
import { getNetworkKey } from '../../utils/keys/network.key.js'
import { useCachedNetwork } from '../cache/useCachedNetwork.js'

export const useUpdateNetworkNotificationSettings = (options?: {
  useMutationOptions?: UseMutationOptions<
    NotificationNetworkSettings,
    ClientError,
    MutationUpdateNetworkNotificationSettingsArgs,
    { snapshot: Network }
  >
}) => {
  const { useMutationOptions } = options || {}
  const { client } = useTribeClient()
  const queryClient = useQueryClient()
  const networkKey = getNetworkKey()
  const networkSnapshot = useCachedNetwork()

  return useMutation<
    NotificationNetworkSettings,
    ClientError,
    MutationUpdateNetworkNotificationSettingsArgs,
    { snapshot: Network }
  >(
    variables =>
      client.notifications.updateNetworkNotificationSettings(variables),
    {
      onMutate: async newNetworkSettings => {
        await queryClient.cancelQueries(networkKey)
        const snapshot = networkSnapshot
        queryClient.setQueriesData<Network>(networkKey, old => {
          return {
            ...old,
            settings: {
              ...old.settings,
              ...{
                emailEnabled:
                  newNetworkSettings?.input?.emailEnabled ??
                  old?.settings?.emailEnabled,
                emailNotificationEnabled:
                  newNetworkSettings?.input?.emailNotificationEnabled ??
                  old?.settings?.emailNotificationEnabled,
                emailFrom:
                  newNetworkSettings?.input?.emailFrom ??
                  old?.settings?.emailFrom,
                emailDigestEnabled:
                  newNetworkSettings?.input?.emailDigestEnabled ??
                  old?.settings?.emailDigestEnabled,
              },
            },
          }
        })

        return { snapshot }
      },
      onError: (err, newNetworkSettings, context) => {
        // revert back the optimistic response
        queryClient.setQueriesData<Network>(networkKey, context.snapshot)
      },
      onSettled: () => {
        // invalidate to refetch again
        queryClient.invalidateQueries(networkKey)
      },
      ...useMutationOptions,
    },
  )
}
